import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import Select from 'react-select';
import {
  Button,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from 'reactstrap';
import { updateFairProfile } from '../../ReduxToolkit/Reducers/fairManagement';
import {
  CATEGORY_OPTIONS,
  MARKET_OPTIONS,
  toCategoryOptions,
  toMarketOptions,
} from './options';

const TEXT_FIELDS = [
  { key: 'name', label: 'Fair name' },
  { key: 'website', label: 'Website' },
  { key: 'primary_audience', label: 'Primary audience' },
  { key: 'facebook', label: 'Facebook' },
  { key: 'instagram', label: 'Instagram' },
  { key: 'linkedin', label: 'LinkedIn' },
];

const toForm = (profile) => ({
  ...Object.fromEntries(TEXT_FIELDS.map(({ key }) => [key, profile?.[key] || ''])),
  founded_year: profile?.founded_year || '',
  countries: toMarketOptions(profile?.countries || []),
  categories: toCategoryOptions(profile?.categories || []),
});

// The fair's Edit Profile fields, minus logo and cover images (those stay the fair's own)
const FairDetailsModal = ({ open, profile, onClose, onSaved }) => {
  const dispatch = useDispatch();
  const [form, setForm] = useState(toForm(profile));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(toForm(profile));
  }, [open, profile]);

  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    const result = await dispatch(
      updateFairProfile(profile.id, {
        ...Object.fromEntries(TEXT_FIELDS.map(({ key }) => [key, form[key].trim()])),
        founded_year: form.founded_year ? Number(form.founded_year) : null,
        categories: form.categories.map((o) => o.value),
        // At least one market is required once a fair has them; none picked = left unchanged
        ...(form.countries.length && { countries: form.countries.map((o) => o.value) }),
      })
    );
    setSaving(false);
    if (result) {
      onSaved?.(result);
      onClose();
    }
  };

  return (
    <Modal isOpen={open} toggle={saving ? undefined : onClose} centered size="lg">
      <ModalHeader toggle={saving ? undefined : onClose}>Edit fair details</ModalHeader>
      <ModalBody>
        <Row>
          {TEXT_FIELDS.map(({ key, label }) => (
            <Col md={6} key={key}>
              <FormGroup>
                <Label>{label}</Label>
                <Input value={form[key]} onChange={(e) => set(key, e.target.value)} />
              </FormGroup>
            </Col>
          ))}
          <Col md={6}>
            <FormGroup>
              <Label>Founded year</Label>
              <Input
                type="number"
                min={1800}
                max={2100}
                value={form.founded_year}
                onChange={(e) => set('founded_year', e.target.value)}
              />
            </FormGroup>
          </Col>
          <Col md={12}>
            <FormGroup>
              <Label>Markets (New Profiles countries)</Label>
              <Select
                isMulti
                options={MARKET_OPTIONS}
                value={form.countries}
                onChange={(value) => set('countries', value || [])}
                closeMenuOnSelect={false}
              />
            </FormGroup>
          </Col>
          <Col md={12}>
            <FormGroup>
              <Label>Exhibition categories</Label>
              <Select
                isMulti
                options={CATEGORY_OPTIONS}
                value={form.categories}
                onChange={(value) => set('categories', value || [])}
                closeMenuOnSelect={false}
              />
            </FormGroup>
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button color="primary" outline onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button color="primary" onClick={handleSave} disabled={saving || !form.name.trim()}>
          {saving ? <Spinner size="sm" color="light" /> : 'Save'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default FairDetailsModal;
